import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateProductDetails } from '../../hooks/api/products';
import useNotificationContext from '../../hooks/notification/useNotificationContext';
import { UpdateProductPermissionErrorCode } from '../../shared/enums/permission-error-code';
import { ToastSeverity } from '../../shared/enums/toast-severity';
import { InplaceEditContext } from './ProductDetailsPageInplaceEditContext';

type ProductDetailsPageInplaceEditProviderProps = {
  children: React.ReactNode;
};

export const ProductDetailsPageInplaceEditProvider = ({
  children,
}: ProductDetailsPageInplaceEditProviderProps) => {
  const [productId, setProductId] = useState<string | null>(null);

  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { notify } = useNotificationContext();
  const { t } = useTranslation();

  const activate = (fieldId: string) => {
    // Prevent activation of the same field
    if (activeFieldId === fieldId) {
      return;
    }

    // Prevent activation when saving another field
    if (isSaving) {
      return;
    }

    setActiveFieldId(fieldId);
  };

  const deactivate = () => {
    setActiveFieldId(null);
  };

  const handleSave = async <T,>(id: string, fieldId: string, newValue: T) => {
    updateProductDetails({
      id,
      [fieldId]: newValue,
    });
  };

  const { mutate: updateProductDetails, isLoading: isSaving } = useUpdateProductDetails({
    onSuccess: () => {
      // Invalidate the query to refetch the product details
      queryClient.invalidateQueries({ queryKey: ['products', productId?.toString()] });

      // Invalidate the query to refetch components
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => {
      const message = (error as Error)?.message;
      if (
        message === UpdateProductPermissionErrorCode.UPDATE_PRODUCT_RESELLER_CANNOT_UPDATE_PRODUCT
      ) {
        // Handle specific error case where reseller cannot update product
        notify({
          severity: ToastSeverity.ERROR,
          detail: t('toastMessages.updateProduct.resellerUpdateProductErrorMessage'),
          life: 5000,
        });
      } else if (
        message === UpdateProductPermissionErrorCode.UPDATE_PRODUCT_ALREADY_EXIST_WITH_PRODUCT_ID
      ) {
        // Handle specific error case where product Id and location combination already exists
        notify({
          severity: ToastSeverity.ERROR,
          detail: t('toastMessages.updateProduct.productIdLocationCombinationExist'),
          life: 5000,
        });
      } else {
        // Handle other generic errors
        notify({
          severity: ToastSeverity.ERROR,
          detail: t('toastMessages.updateProduct.updateFailedErrorMessage'),
          life: 5000,
        });
      }
      // Emit an event to the InplaceEdit component to rollback to the previous value
      window.dispatchEvent(new Event(`inplace-edit:${activeFieldId}:rollback`));
    },
    onSettled: deactivate,
  });

  return (
    <InplaceEditContext.Provider
      value={{
        productId,
        setProductId,
        activeFieldId,
        activate,
        deactivate,
        handleSave,
        isSaving,
      }}
    >
      {children}
    </InplaceEditContext.Provider>
  );
};

export default ProductDetailsPageInplaceEditProvider;
